import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAddGitRepositoryMutation } from 'app/createApi';
import { ArrowTopRightOnSquareIcon, ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import {
  selectDbtProjectFilename,
  selectDbtVersion,
  selectSource,
  selectHasToUploadProject,
  selectDbtCloudImportStatus,
  selectRepositoryUrl,
  selectSshKeyId,
  selectPublicKey,
  selectIsPublicKeyCheckboxChecked,
  setDbtProjectFilename,
  setDbtVersion,
  setSelectorTags,
  setSelectorFiles,
  setSource,
  setHasToUploadProject,
  setDbtCloudImportStatus,
  setRepositoryUrl,
  setSshKeyId,
  setPublicKey,
  setIsPublicKeyCheckboxChecked,
} from '../reducers/dbtMigrationSlice';
import { FolderIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import StepWrapper from 'components/StepWrapper';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import DropdownSingleselect from 'components/DropdownSingleselect';
import { getConfig } from 'config/config-helper';
import { classNames } from 'utils/styleUtils';

const { dataopsliveBaseUrl } = getConfig();

const supportedDbtVersions = ['1.4', '1.5', '1.6', '1.7', '1.8', '1.9'];

const sourceToTabMap = {
  upload: 'Upload project',
  sample: 'Use example',
  'dbt-cloud': 'dbt™ Cloud',
};

const tabs = Object.values(sourceToTabMap);

interface ProjectUploadStatsProps {
  responseMessage?: string;
  numberofFilesUploaded?: number;
}

function ProjectUploadStats({ responseMessage, numberofFilesUploaded }: ProjectUploadStatsProps): JSX.Element {
  return (
    <div
      className={classNames(
        responseMessage === 'Compile unsuccessful.' ? 'bg-yellow-50' : 'bg-green-50',
        'w-full mx-[auto] rounded-md p-4',
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {responseMessage === 'Compile unsuccessful.' ? (
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
          ) : (
            <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
          )}
        </div>
        <div className="ml-3">
          <h3
            className={classNames(
              responseMessage === 'Compile unsuccessful.' ? 'text-yellow-800' : 'text-green-800',
              'text-sm font-medium',
            )}
          >
            {responseMessage === 'Compile unsuccessful.'
              ? 'Project successfully uploaded, but the compilation failed'
              : 'Project successfully uploaded'}
          </h3>
          <div className="flex flex-wrap">
            {responseMessage !== undefined && responseMessage !== '' && (
              <div
                className={classNames(
                  responseMessage === 'Compile unsuccessful.' ? 'text-yellow-700' : 'text-green-700',
                  'mt-2 mr-6 text-sm',
                )}
              >
                <p>
                  <span
                    className={classNames(
                      responseMessage === 'Compile unsuccessful.' ? 'text-yellow-900' : 'text-green-900',
                      'font-semibold',
                    )}
                  >
                    Message:
                  </span>{' '}
                  {responseMessage === 'Compile unsuccessful.'
                    ? 'After finishing the flow, please use our Develop cloud IDE to fix the errors in the project. It comes with an AI assistant that will help you along the way!'
                    : responseMessage}
                </p>
              </div>
            )}
            {numberofFilesUploaded !== undefined && (
              <div
                className={classNames(
                  responseMessage === 'Compile unsuccessful.' ? 'text-yellow-700' : 'text-green-700',
                  'mt-2 text-sm',
                )}
              >
                <p className="whitespace-nowrap">
                  <span
                    className={classNames(
                      responseMessage === 'Compile unsuccessful.' ? 'text-yellow-900' : 'text-green-900',
                      'font-semibold',
                    )}
                  >
                    Files uploaded:
                  </span>{' '}
                  {numberofFilesUploaded}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface SourceProjectStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function SourceProjectStep(props: SourceProjectStepProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [loadingAddRepository, setLoadingAddRepository] = useState<boolean>(false);
  const [loadingDbtCloudImport, setLoadingDbtCloudImport] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to upload project');

  const [responseMessage, setResponseMessage] = useState<string>();
  const [numberofFilesUploaded, setNumberOfFilesUploaded] = useState<number>();

  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const dbtProjectFilename = useAppSelector(selectDbtProjectFilename);
  const dbtVersion = useAppSelector(selectDbtVersion);
  const source = useAppSelector(selectSource);
  const hasToUploadProject = useAppSelector(selectHasToUploadProject);
  const dbtCloudImportStatus = useAppSelector(selectDbtCloudImportStatus);
  const repositoryUrl = useAppSelector(selectRepositoryUrl);
  const sshKeyId = useAppSelector(selectSshKeyId);
  const publicKey = useAppSelector(selectPublicKey);
  const isPublicKeyCheckboxChecked = useAppSelector(selectIsPublicKeyCheckboxChecked);
  const [filename, setFilename] = useState<string>(dbtProjectFilename);
  const [copyKeySuccess, setCopyKeySuccess] = useState<boolean>(false);

  const [addGitRepository] = useAddGitRepositoryMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files !== null) {
      setFilename(e.target.files?.[0].name);
      dispatch(setDbtProjectFilename(e.target.files?.[0].name));
      onProjectUpload(false, e.target.files?.[0].name);
    }
  };

  const catchError = (error: any): void => {
    console.log('dbt-upload failed');
    setFailed(true);
    setErrorMessage(error.message);
    setSuccess(false);
    setLoading(false);
    setLoadingUpload(false);
  };

  const onProjectUpload = (continueOnSuccess: boolean, zipFilename: string): void => {
    dispatch(setSource('upload'));
    if (fileInputRef !== null) {
      setLoading(true);
      if (!continueOnSuccess) {
        setLoadingUpload(true);
      }
      setFailed(false);
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        const data = event.target?.result;
        const uploadUrl = `/api/v1/dbt-upload/process?branch=${branch}&repository=${dataopsliveBaseUrl}/${projectPath
          .toLowerCase()
          .split(' ')
          .join('-')}.git&dbt=${dbtVersion}`;

        if (event.target !== null) {
          console.log('Upload successful');
          axios
            .get('/api/v1/dbt-upload/url')
            .then((urlRes: any) => {
              const form = new FormData();
              for (const key in urlRes.data.fields) {
                form.append(key, urlRes.data.fields[key]);
              }
              form.append('file', new Blob([data as ArrayBuffer], { type: 'application/zip' }));
              axios
                .post(urlRes.data.url, form, {
                  transformRequest: (data, headers) => {
                    delete headers.common;
                    delete headers.Authorization;
                    return data;
                  },
                })
                .then((uploadRes: any) => {
                  const key = String(urlRes.data.fields.key);
                  const processUrl = `${uploadUrl}&file_key=${key}`;
                  axios
                    .get(processUrl)
                    .then((res: any) => {
                      console.log(res.data);

                      if (res.data.objects_stats !== undefined) {
                        setResponseMessage(res.data.objects_stats);
                      }

                      if (res.data.selectors?.tags !== undefined) {
                        dispatch(setSelectorTags(res.data.selectors?.tags));
                      }

                      if (res.data.selectors?.files !== undefined) {
                        setNumberOfFilesUploaded(res.data.selectors?.files?.length);
                        dispatch(setSelectorFiles(res.data.selectors?.files));
                      }

                      dispatch(setHasToUploadProject(false));
                      setSuccess(true);
                    })
                    .catch(catchError)
                    .finally(() => {
                      setLoading(false);
                      if (!continueOnSuccess) {
                        setLoadingUpload(false);
                      }
                    });
                })
                .catch(catchError);
            })
            .catch(catchError);
        }
      };
      if (fileInputRef.current?.files?.[0] !== undefined) {
        fileReader.readAsArrayBuffer(fileInputRef.current?.files?.[0]);
      }
    }
  };

  const onAddGitRepository = (): void => {
    setLoadingAddRepository(true);
    setFailed(false);

    addGitRepository({ gitUrl: repositoryUrl })
      .unwrap()
      .then((res: any) => {
        console.log('Git repository added:', res);
        if (res.ssh_public_key !== undefined && res.ssh_key_id !== undefined) {
          dispatch(setPublicKey(res.ssh_public_key));
          dispatch(setSshKeyId(res.ssh_key_id));
          dispatch(setDbtCloudImportStatus('repo-added'));
        } else {
          setErrorMessage('Failed to get SSH public key');
          setFailed(true);
        }
      })
      .catch((error) => {
        console.error('Failed to add git repository:', error);
        setErrorMessage(error.message !== undefined ? error.message : 'Failed to add git repository');
        setFailed(true);
      })
      .finally(() => {
        setLoadingAddRepository(false);
      });
  };

  const onDbtCloudImport = (): void => {
    setLoadingDbtCloudImport(true);
    setFailed(false);

    const requestBody = {
      git_url: repositoryUrl,
      ssh_key_id: sshKeyId,
      repository: `${dataopsliveBaseUrl}/${projectPath.toLowerCase().split(' ').join('-')}`,
      dbt: dbtVersion,
    };

    axios
      .post('/api/v1/dbt-upload/git-ssh', requestBody)
      .then((res: any) => {
        console.log(res.data);

        if (res.data.objects_stats !== undefined) {
          setResponseMessage(res.data.objects_stats);
        }

        if (res.data.selectors?.tags !== undefined) {
          dispatch(setSelectorTags(res.data.selectors?.tags));
        }

        if (res.data.selectors?.files !== undefined) {
          setNumberOfFilesUploaded(res.data.selectors?.files?.length);
          dispatch(setSelectorFiles(res.data.selectors?.files));
        }

        setSuccess(true);
        dispatch(setHasToUploadProject(false));
        dispatch(setDbtCloudImportStatus('import-completed'));
      })
      .catch(catchError)
      .finally(() => {
        setLoadingDbtCloudImport(false);
      });
  };

  const onContinue = (): void => {
    if (source === 'sample') {
      setLoading(true);
      setFailed(false);

      setFilename('jaffle-shop-main.zip');

      const sampleUrl = `/api/v1/dbt-upload/sample?sample_project=jaffle-shop-main.zip&branch=${branch}&repository=${dataopsliveBaseUrl}/${projectPath
        .toLowerCase()
        .split(' ')
        .join('-')}.git&dbt=${dbtVersion}`;

      axios
        .get(sampleUrl)
        .then((res: any) => {
          console.log(res.data);

          dispatch(setDbtProjectFilename('jaffle-shop-main.zip'));

          if (res.data.objects_stats !== undefined) {
            setResponseMessage(res.data.objects_stats);
          }

          if (res.data.selectors?.tags !== undefined) {
            dispatch(setSelectorTags(res.data.selectors?.tags));
          }

          if (res.data.selectors?.files !== undefined) {
            setNumberOfFilesUploaded(res.data.selectors?.files?.length);
            dispatch(setSelectorFiles(res.data.selectors?.files));
          }

          setSuccess(true);

          props.onContinue();
        })
        .catch(catchError)
        .finally(() => {
          setLoading(false);
        });
    } else if (hasToUploadProject) {
      onProjectUpload(true, filename);
    } else {
      props.onContinue();
    }
  };

  return (
    <StepWrapper
      title="Source project"
      subtitle="Select a source of your dbt project"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      continueDisabled={
        (source === 'upload' && dbtProjectFilename === '') ||
        (source === 'dbt-cloud' && dbtCloudImportStatus !== 'import-completed') ||
        loading ||
        loadingUpload
      }
      isLoading={loading && !loadingUpload}
      isFailed={failed}
      errorMessage={errorMessage}
    >
      <div className="w-full xl:w-3/4 wide:w-1/2 mx-[auto] flex flex-col items-center mb-2">
        <div className="w-4/5 mx-[auto]">
          <div className="border-b border-gray-200 mb-6">
            <nav aria-label="Tabs" className="-mb-px flex justify-between rounded-md">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  onClick={() => {
                    if (source === 'sample') {
                      dispatch(setDbtProjectFilename(''));
                      setFilename('');
                    }
                    dispatch(
                      setSource(tab === 'Use example' ? 'sample' : tab === 'dbt™ Cloud' ? 'dbt-cloud' : 'upload'),
                    );
                    setFailed(false);
                    setSuccess(false);
                    setLoading(false);
                    setLoadingUpload(false);
                  }}
                  className={classNames(
                    (tab === 'Upload project' && source === 'upload') ||
                      (tab === 'Use example' && source === 'sample') ||
                      (tab === 'dbt™ Cloud' && source === 'dbt-cloud')
                      ? 'border-dataops-primary text-dataops-primary bg-dataops-primary/5'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'flex-1 whitespace-nowrap border-b-2 px-1 py-2 text-center text-sm font-medium cursor-pointer',
                  )}
                >
                  {tab}
                </div>
              ))}
            </nav>
          </div>
        </div>
        <div className="w-full flex flex-col items-center space-y-4">
          {source === 'upload' && (
            <>
              <div className="w-4/5 mx-[auto] text-sm text-gray-600">
                Upload a ZIP file containing your dbt project and choose the dbt version you are using.
              </div>
              <div className="w-full flex justify-evenly items-center">
                <div>
                  <DropdownSingleselect
                    label="dbt version"
                    options={supportedDbtVersions}
                    selected={dbtVersion}
                    setSelected={(value) => {
                      dispatch(setDbtVersion(value));
                      dispatch(setHasToUploadProject(true));
                    }}
                  />
                </div>
              </div>
              <div className="w-4/5 mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                <div className="text-center flex flex-col items-center">
                  <FolderIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                  <p className="text-sm text-gray-800">Upload your own dbt project</p>
                  <p className="text-sm text-gray-500">or use a ready-to-go example project to get started</p>
                  {filename !== '' && (
                    <>
                      <div className="flex items-center mt-4">
                        <div className="text-sm text-gray-600 font-semibold">{filename}</div>
                        {loadingUpload && !failed && (
                          <div className="ml-2 min-h-4 flex items-center justify-center">
                            <div
                              className="animate-spin inline-block w-4 h-4 border-[1.5px] border-current border-t-transparent text-dataops-primary rounded-full"
                              role="status"
                              aria-label="loading"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {loadingUpload && !failed && (
                        <div className="text-sm text-dataops-primary">
                          Uploading and compiling your dbt™ project
                          <span className="inline-flex ml-1">
                            <span className="opacity-0 animate-[blink_1s_ease-in-out_0s_infinite]">.</span>
                            <span className="opacity-0 animate-[blink_1s_ease-in-out_0.3s_infinite]">.</span>
                            <span className="opacity-0 animate-[blink_1s_ease-in-out_0.6s_infinite]">.</span>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="mt-4 flex flex-col items-center justify-center space-y-3 w-full max-w-md">
                    <label htmlFor="file-upload" className="relative cursor-pointer w-full">
                      <div
                        className={classNames(
                          filename === ''
                            ? 'bg-dataops-primary hover:bg-dataops-primary-hover text-white'
                            : 'bg-white font-semibold text-gray-900 shadow-sm hover:bg-gray-50 ring-1 ring-inset ring-gray-300',
                          'rounded-md w-full px-3 py-2 text-center text-sm font-semibold shadow-sm disabled:bg-gray-400',
                        )}
                      >
                        {filename === '' ? 'Upload' : 'Change'}
                      </div>
                      <input
                        type="file"
                        accept=".zip"
                        onChange={onFileChange}
                        ref={fileInputRef}
                        id="file-upload"
                        name="file-upload"
                        className="sr-only"
                      />
                    </label>
                    <button
                      className="rounded-md w-full px-3 py-2 text-center text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 ring-1 ring-inset ring-gray-300"
                      onClick={() => dispatch(setSource('sample'))}
                    >
                      Use example
                    </button>
                    <button
                      className="rounded-md w-full px-3 py-2 text-center text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 ring-1 ring-inset ring-gray-300"
                      onClick={() => {
                        dispatch(setSource('dbt-cloud'));
                      }}
                    >
                      dbt™ Cloud
                    </button>
                  </div>
                </div>
              </div>
              {success && (
                <div className="w-4/5 mx-[auto]">
                  <ProjectUploadStats responseMessage={responseMessage} numberofFilesUploaded={numberofFilesUploaded} />
                </div>
              )}
            </>
          )}

          {source === 'sample' && (
            <div className="w-4/5 mx-[auto] flex flex-col items-center">
              <div className="mb-4 text-sm text-gray-600">
                Use a ready-to-go dbt project to get started. Using this project requires no additional setup. Press
                Continue to proceed.
              </div>
              <div className="w-full relative max-w-[25rem] mx-[auto] mt-4 overflow-hidden rounded-xl border border-gray-400">
                <div className="absolute top-3 right-3">
                  <div className="h-3 w-3 rounded-full bg-dataops-primary"></div>
                </div>
                <div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 px-4 py-2">
                  <img
                    alt="dbt logo"
                    src="/static/dbt-logo.png"
                    className="size-12 p-1 object-contain flex-none rounded-full bg-white ring-1 ring-gray-900/10"
                  />
                  <a className="flex" href="https://github.com/dbt-labs/jaffle-shop" target="_blank" rel="noreferrer">
                    <div className="text-xl font-[550] text-gray-900">Jaffle Shop</div>
                    <ArrowTopRightOnSquareIcon className="size-3 ml-0.5 mt-1 text-gray-900" />
                  </a>
                </div>
                <div className="divide-y divide-gray-100 px-4 py-2 text-sm/6 text-gray-600">
                  This is a sandbox project for exploring the basic functionality and latest features of dbt. It&apos;s
                  based on a fictional restaurant called the Jaffle Shop that serves jaffles.
                </div>
              </div>
            </div>
          )}

          {source === 'dbt-cloud' && (
            <div className="w-4/5 mx-[auto] flex flex-col items-center">
              <div className="text-sm text-gray-600">
                To import your dbt™ Cloud project, enter your repository URL (e.g., GitHub or GitLab) to get started. If
                you are using a dbt™ Cloud-managed repo, you should first migrate it to a Git repository.
              </div>
              <div className="w-full grid grid-cols-4 gap-4 mt-4">
                <div className="col-span-3">
                  <label htmlFor="repository-url" className="block text-sm font-medium leading-6 text-gray-900">
                    Repository URL*
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id="repository-url"
                      className="block w-full rounded-md flex-1 ring-1 ring-inset ring-gray-300 border-0 bg-transparent py-1.5 px-1.5 text-black placeholder:text-gray-400 sm:text-sm min-h-[36px]"
                      placeholder="git@github.com:username/repo.git"
                      value={repositoryUrl}
                      onChange={(e) => dispatch(setRepositoryUrl(e.target.value))}
                      required
                    />
                  </div>
                </div>
                <div className="col-span-1">
                  <DropdownSingleselect
                    label="dbt version"
                    options={supportedDbtVersions}
                    selected={dbtVersion}
                    setSelected={(value) => {
                      dispatch(setDbtVersion(value));
                    }}
                  />
                </div>
              </div>

              {(dbtCloudImportStatus === 'repo-added' || dbtCloudImportStatus === 'import-completed') && (
                <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">Public Key Generated</h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Please add this public key to your Git provider&apos;s SSH keys section:
                  </p>
                  <div className="bg-gray-100 p-2 pr-10 rounded-md text-sm font-mono relative break-all whitespace-pre-wrap">
                    {publicKey}
                    <button
                      type="button"
                      className="absolute top-1 right-1 p-1 rounded-md border bg-gray-50 hover:bg-white"
                      onClick={() => {
                        void (async () => {
                          try {
                            await navigator.clipboard.writeText(publicKey);
                            setCopyKeySuccess(true);
                            setTimeout(() => setCopyKeySuccess(false), 1500);
                          } catch (err) {
                            console.error('Failed to copy text: ', err);
                          }
                        })();
                      }}
                      title="Copy to clipboard"
                    >
                      {copyKeySuccess ? (
                        <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />
                      ) : (
                        <ClipboardIcon className="h-4 w-4 text-gray-600" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center">
                    <input
                      id="public-key-added"
                      name="public-key-added"
                      type="checkbox"
                      checked={isPublicKeyCheckboxChecked}
                      onChange={(e) => dispatch(setIsPublicKeyCheckboxChecked(e.target.checked))}
                      className="h-4 w-4 rounded border-gray-300 text-dataops-primary focus:ring-dataops-primary"
                    />
                    <label htmlFor="public-key-added" className="ml-2 text-sm text-blue-700">
                      I&apos;ve added the public key to my Git provider
                    </label>
                  </div>
                </div>
              )}

              <div className="w-full mt-4">
                {/* Initial state - Connect Repository button */}
                {dbtCloudImportStatus === 'waiting-for-url' && (
                  <button
                    type="button"
                    className="w-full rounded-md bg-dataops-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-dataops-primary-hover disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={onAddGitRepository}
                    disabled={loadingAddRepository || repositoryUrl === ''}
                  >
                    {loadingAddRepository ? (
                      <div className="flex items-center justify-center">
                        <div
                          className="animate-spin inline-block w-4 h-4 border-[1.5px] border-current border-t-transparent text-white rounded-full mr-2"
                          role="status"
                          aria-label="loading"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                        Connecting...
                      </div>
                    ) : (
                      'Connect Repository'
                    )}
                  </button>
                )}

                {/* Public key added - Import Project button */}
                {(dbtCloudImportStatus === 'repo-added' || dbtCloudImportStatus === 'import-completed') && (
                  <button
                    type="button"
                    className={`w-full rounded-md px-3 py-2 text-center text-sm font-semibold shadow-sm ${
                      dbtCloudImportStatus === 'import-completed'
                        ? 'text-gray-900 hover:bg-gray-50 ring-1 ring-inset ring-gray-300'
                        : 'bg-dataops-primary text-white hover:bg-dataops-primary-hover disabled:bg-gray-400 disabled:cursor-not-allowed'
                    }`}
                    onClick={onDbtCloudImport}
                    disabled={loadingDbtCloudImport || !isPublicKeyCheckboxChecked}
                  >
                    {loadingDbtCloudImport ? (
                      <div className="flex items-center justify-center">
                        <div
                          className="animate-spin inline-block w-4 h-4 border-[1.5px] border-current border-t-transparent text-white rounded-full mr-2"
                          role="status"
                          aria-label="loading"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                        Importing...
                      </div>
                    ) : (
                      'Import Project'
                    )}
                  </button>
                )}
              </div>

              {/* Show success message when import is completed */}
              {dbtCloudImportStatus === 'import-completed' && success && (
                <div className="mt-4">
                  <ProjectUploadStats responseMessage={responseMessage} numberofFilesUploaded={numberofFilesUploaded} />
                </div>
              )}
            </div>
          )}

          <div className="w-4/5 mx-[auto]">
            <LoadingAndErrorSection
              isLoading={loading}
              isFailed={failed}
              errorMessage={errorMessage}
              hideLoading={true}
            />
          </div>
        </div>
      </div>
    </StepWrapper>
  );
}
